import React, { useEffect, useMemo, useRef, useState } from 'react';

import { UsersAPI } from '../lib/api';
import { rememberAssignees, loadRecentAssignees } from '../lib/recentAssignees';
import { formatAssigneeLabel } from '../lib/assignees';
import {
  defaultSuggestions,
  displayMeta,
  isExternalAssignee,
  normalizeUser,
  searchSuggestions,
  unwrapUserList,
} from '../lib/userSearch.js';

const EMPTY_USERS = [];

/**
 * Assignee picker with typeahead.
 *
 * Used on create/edit task (multi-select) and complaint assign/reassign
 * (single-select). Organisations can have far more users than fit in a
 * scrolling list, and Ctrl/Cmd-click is easy to miss.
 *
 * Behaviour
 * ---------
 *  * Selected people render as removable chips.
 *  * Focusing the input with an empty query shows **3 recents** (the last
 *    assignees picked on this browser), padded from the user directory when
 *    fewer than three recents exist.
 *  * Typing searches name, email and mobile via
 *    ``GET /api/super_admin/users/?search=&external=false``.
 *    Portal (``auretics-users``) accounts are excluded server-side and
 *    again in the client so Recents cannot leak them.
 *  * ``multiple`` (default true): picking a suggestion adds it without
 *    closing the menu, so several people can be assigned in one pass.
 *  * ``multiple={false}``: picking replaces the current assignee and closes
 *    the menu — complaints have a single ``assign_to`` FK.
 *
 * Props
 * -----
 *  - ``id``          : id of the visible search input (matches ``<label htmlFor>``).
 *  - ``value``       : array of selected user PKs.
 *  - ``onChange(ids)`` : called with the next PK array.
 *  - ``knownUsers``  : optional seed rows (current user, existing assignees)
 *                      so chips have a label before a search returns.
 *  - ``multiple``    : allow more than one selection. Default true.
 *  - ``selfId``      : logged-in user PK so that person is labelled Self.
 *  - ``placeholder``, ``disabled``.
 */
export default function UserSearchSelect({
  id,
  value = [],
  onChange,
  knownUsers = EMPTY_USERS,
  placeholder = 'Search by name, mobile or email',
  disabled = false,
  multiple = true,
  selfId = null,
}) {
  const selectedIds = useMemo(
    () => (value || []).map(Number).filter((n) => Number.isFinite(n)),
    [value],
  );

  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [recents, setRecents] = useState(() => loadRecentAssignees());
  const [results, setResults] = useState([]);
  const [picked, setPicked] = useState([]);
  const [activeIdx, setActiveIdx] = useState(0);

  const wrapperRef = useRef(null);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);
  const abortRef = useRef(null);

  useEffect(() => {
    function onDocClick(e) {
      if (!wrapperRef.current) return;
      if (!wrapperRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  useEffect(() => () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (abortRef.current) abortRef.current.abort();
  }, []);

  const cache = useMemo(
    () => mergeCache({}, [...knownUsers, ...recents, ...results, ...picked]),
    [knownUsers, recents, results, picked],
  );

  const suggestions = useMemo(() => {
    const trimmed = query.trim();
    if (!trimmed) {
      return defaultSuggestions({
        recents,
        fallback: results,
        excludeIds: selectedIds,
      });
    }
    return searchSuggestions({
      query: trimmed,
      recents,
      results,
      excludeIds: selectedIds,
    });
  }, [query, recents, results, selectedIds]);

  const selectedUsers = useMemo(
    () =>
      selectedIds.map((idValue) => cache[idValue] || { id: idValue }),
    [selectedIds, cache],
  );

  const triggerSearch = (text) => {
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);

    setLoading(true);
    setErrorMsg('');

    debounceRef.current = setTimeout(async () => {
      const ctrl = new AbortController();
      abortRef.current = ctrl;
      try {
        const data = await UsersAPI.list(
          { search: text, page_size: 20, external: 'false' },
          { signal: ctrl.signal, silent: true },
        );
        const rows = unwrapUserList(data)
          .filter((row) => !isExternalAssignee(row))
          .map(normalizeUser)
          .filter(Boolean);
        setResults(rows);
        setActiveIdx(0);
      } catch (err) {
        if (err && (err.name === 'CanceledError' || err.code === 'ERR_CANCELED')) {
          return;
        }
        setResults([]);
        setErrorMsg('Could not load users.');
      } finally {
        if (abortRef.current === ctrl) {
          abortRef.current = null;
          setLoading(false);
        }
      }
    }, 250);
  };

  const onInput = (e) => {
    const text = e.target.value;
    setQuery(text);
    setOpen(true);
    triggerSearch(text.trim());
  };

  const onFocus = () => {
    setOpen(true);
    if (results.length === 0 && !loading) {
      triggerSearch(query.trim());
    }
  };

  const pick = (user) => {
    if (!user || selectedIds.includes(user.id)) return;
    const next = multiple ? [...selectedIds, user.id] : [user.id];
    setPicked((prev) => (prev.some((row) => row.id === user.id) ? prev : [...prev, user]));
    setRecents(rememberAssignees([user]));
    onChange(next);
    setQuery('');
    setActiveIdx(0);
    if (multiple) {
      triggerSearch('');
      inputRef.current?.focus();
    } else {
      setOpen(false);
    }
  };

  const remove = (idValue) => {
    onChange(selectedIds.filter((idRow) => idRow !== idValue));
    inputRef.current?.focus();
  };

  const onKeyDown = (e) => {
    if (!open && (e.key === 'ArrowDown' || e.key === 'Enter')) {
      setOpen(true);
      triggerSearch(query.trim());
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx((i) => Math.min(suggestions.length - 1, i + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx((i) => Math.max(0, i - 1));
    } else if (e.key === 'Enter') {
      if (activeIdx >= 0 && suggestions[activeIdx]) {
        e.preventDefault();
        pick(suggestions[activeIdx]);
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
    } else if (e.key === 'Backspace' && !query && selectedIds.length) {
      remove(selectedIds[selectedIds.length - 1]);
    }
  };

  const heading = query.trim()
    ? 'Suggestions'
    : recents.length
      ? 'Recent'
      : 'Suggestions';

  const nameOf = (user) => formatAssigneeLabel(user, selfId);

  return (
    <div ref={wrapperRef} className="ty-user-search">
      <div
        className={`ty-user-search__box${disabled ? ' is-disabled' : ''}${open ? ' is-open' : ''}`}
        onMouseDown={(e) => {
          if (e.target === e.currentTarget) inputRef.current?.focus();
        }}
      >
        {selectedUsers.map((user) => (
          <span key={user.id} className="ty-user-search__chip">
            <span className="ty-user-search__chip-label">{nameOf(user)}</span>
            <button
              type="button"
              className="ty-user-search__chip-remove"
              aria-label={`Remove ${nameOf(user)}`}
              disabled={disabled}
              onClick={() => remove(user.id)}
            >
              ×
            </button>
          </span>
        ))}
        <input
          id={id}
          ref={inputRef}
          type="text"
          className="ty-user-search__input"
          value={query}
          onChange={onInput}
          onFocus={onFocus}
          onKeyDown={onKeyDown}
          placeholder={
            selectedIds.length
              ? (multiple ? 'Add another…' : '')
              : placeholder
          }
          autoComplete="off"
          disabled={disabled}
          aria-autocomplete="list"
          aria-expanded={open ? 'true' : 'false'}
          aria-controls={id ? `${id}-listbox` : undefined}
        />
      </div>

      {open && (
        <div
          id={id ? `${id}-listbox` : undefined}
          role="listbox"
          className="ty-user-search__menu"
        >
          <div className="ty-user-search__heading">{heading}</div>

          {loading && suggestions.length === 0 && (
            <div className="ty-user-search__hint">Searching…</div>
          )}

          {!loading && errorMsg && (
            <div className="ty-user-search__hint ty-user-search__hint--error" role="alert">
              {errorMsg}
            </div>
          )}

          {!loading && !errorMsg && suggestions.length === 0 && (
            <div className="ty-user-search__hint">
              {query.trim() ? 'No users match that search.' : 'No more people to suggest.'}
            </div>
          )}

          {!errorMsg && suggestions.length > 0 && (
            <ul className="ty-user-search__list">
              {suggestions.map((user, idx) => {
                const isActive = idx === activeIdx;
                const meta = displayMeta(user);
                return (
                  <li
                    key={user.id}
                    role="option"
                    aria-selected={isActive ? 'true' : 'false'}
                    className={`ty-user-search__option${isActive ? ' is-active' : ''}`}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      pick(user);
                    }}
                    onMouseEnter={() => setActiveIdx(idx)}
                  >
                    <div className="ty-user-search__option-name">{nameOf(user)}</div>
                    {meta ? (
                      <div className="ty-user-search__option-meta">{meta}</div>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

function mergeCache(prev, rows) {
  const next = { ...prev };
  for (const raw of rows || []) {
    const user = normalizeUser(raw);
    if (!user) continue;
    const existing = next[user.id];
    next[user.id] = existing
      ? {
          ...existing,
          ...Object.fromEntries(
            Object.entries(user).filter(([, v]) => v != null && v !== ''),
          ),
        }
      : user;
  }
  return next;
}
